// client/src/components/modals/RegisterVehicleModal.js
import React, { useState, useMemo } from 'react';
import { Modal, Button, Form, Spinner, Alert, Row, Col } from 'react-bootstrap';
import api from '../../api/axios'; // Use centralized api instance

const RegisterVehicleModal = ({ show, onHide, customerId, isCarModelRequired, carModels, onSuccess }) => {
    const [plateNumber, setPlateNumber] = useState('');
    const [selectedManufacturer, setSelectedManufacturer] = useState('');
    const [selectedCarModelId, setSelectedCarModelId] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const uniqueManufacturers = useMemo(() => {
        if (!carModels) return [];
        // API가 displayOrder 순으로 반환 → 등장 순서 유지
        const seen = new Set();
        const list = [];
        carModels.forEach(m => {
            if (m.manufacturer && !seen.has(m.manufacturer)) {
                seen.add(m.manufacturer);
                list.push(m.manufacturer);
            }
        });
        return list;
    }, [carModels]);

    const modelsByManufacturer = useMemo(() => {
        if (!selectedManufacturer) return [];
        return carModels.filter(m => m.manufacturer === selectedManufacturer).sort((a, b) => a.displayOrder - b.displayOrder || a.modelName.localeCompare(b.modelName));
    }, [carModels, selectedManufacturer]);

    const handleSave = async () => {
        setError('');
        if (!plateNumber) {
            setError('차량 번호를 입력해주세요.');
            return;
        }
        if (isCarModelRequired && !selectedCarModelId) {
            setError('차종을 선택해주세요.');
            return;
        }

        setIsSaving(true);
        try {
            // 선택된 모델의 매장 사이즈 자동 매핑
            const selectedModel = carModels.find(m => String(m.id) === String(selectedCarModelId));
            const autoSizeId = selectedModel?.shopSizeId || null;

            await api.post('/vehicles', {
                plateNumber,
                carModelId: selectedCarModelId || null,
                customer_id: customerId,
                vehicle_size_id: autoSizeId
            });
            onSuccess();
            handleClose();
        } catch (err) {
            const message = err.response?.data?.errors?.[0]?.msg || err.response?.data?.error || '차량 등록에 실패했습니다.';
            setError(message);
            console.error("Failed to register vehicle:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setPlateNumber('');
        setSelectedManufacturer('');
        setSelectedCarModelId('');
        setError('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>신규 차량 등록</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>차량 번호</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="예: 12가1234"
                            value={plateNumber}
                            onChange={(e) => setPlateNumber(e.target.value)}
                        />
                    </Form.Group>
                    {isCarModelRequired && (
                        <Row>
                            <Col md={6} className="mb-3">
                                <Form.Group>
                                    <Form.Label>제조사 선택</Form.Label>
                                    <Form.Select value={selectedManufacturer} onChange={e => { setSelectedManufacturer(e.target.value); setSelectedCarModelId(''); }}>
                                        <option value="">-- 제조사 --</option>
                                        {uniqueManufacturers.map(m => <option key={m} value={m}>{m}</option>)}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Group>
                                    <Form.Label>모델명 선택</Form.Label>
                                    <Form.Select value={selectedCarModelId} onChange={e => setSelectedCarModelId(e.target.value)} disabled={!selectedManufacturer}>
                                        <option value="">-- 모델명 --</option>
                                        {modelsByManufacturer.map(model => <option key={model.id} value={model.id}>{model.modelName} ({model.vehicleClass})</option>)}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    취소
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? <Spinner as="span" size="sm" /> : '저장'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RegisterVehicleModal;
